import React, { useState, useEffect } from "react";
import {
  Layout,
  Button,
  Table,
  Space,
  Modal,
  Input,
  Form,
  Card,
  Typography,
  Popconfirm,
  Upload,
  Switch,
  message,
  Image,
} from "antd";

import {
  PlusOutlined,
  UploadOutlined,
  EditOutlined,
  DeleteOutlined,
  DragOutlined,
} from "@ant-design/icons";
import {
  createService,
  createServiceDetails,
  deleteService,
  deleteServiceDetails,
  getServiceDetails,
  getServices,
  updatePublishStatus,
  updateService,
  updateServiceDetails,
  updateServiceDetailsImage,
  updateServiceDetailsPublish,
  updateServiceImage,
  updateServicePriority,
} from "../services/servicesApi";
import ImageGridSorter from "./ImageGridSorter";

const { Content } = Layout;
const { Title } = Typography;
const { TextArea } = Input;

const Services = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editId, setEditId] = useState(null);

  const [imageBase64, setImageBase64] = useState("");
  const [fileList, setFileList] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const pageSize = 5;

  const [form] = Form.useForm();
  const [isChildModal, setIsChildModal] = useState(false);
  const [parentId, setParentId] = useState(null);
  const [childServices, setChildServices] = useState([]);
  const [childForm] = Form.useForm();
  const [childEditMode, setChildEditMode] = useState(false);
  const [childEditId, setChildEditId] = useState(null);

  const [isOrderView, setIsOrderView] = useState(false);
  const [orderedData, setOrderedData] = useState([]);
      const [previewImage, setPreviewImage] = useState(null);
      const [previewOpen, setPreviewOpen] = useState(false);
      const [imagePreview, setImagePreview] = useState(null);

  // Fetch

  const fetchServices = async (page = 1) => {
    setLoading(true);
    try {
      const res = await getServices(page, pageSize);

      setServices(res.data.data);
      setTotalItems(res.data.pagination.totalItems);
      setCurrentPage(page);
    } catch {
      message.error("Fetch failed");
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchServices(currentPage);
  }, [currentPage]);

  const fetchChildServices = async (id) => {
    try {
      const res = await getServiceDetails(id, 1, 10);

      const updated = res.data.data.map((item) => ({
        ...item,
        imageUrl: item.image,
      }));

      setChildServices(updated);
    } catch {
      message.error("Child fetch failed");
    }
  };

  // Submit

  const handleSubmit = async (values) => {
    try {
      if (isEditMode) {
        await updateService(editId, values);
        message.success("Updated");
      } else {
        await createService({
          ...values,
          image: imageBase64,
        });
        message.success("Added");
      }

      fetchServices(currentPage);

      setIsModalVisible(false);
      setIsEditMode(false);
      form.resetFields();
      setFileList([]);
      setImageBase64("");
    } catch {
      message.error("Error");
    }
  };
  const handleSaveOrder = async () => {
    try {
      const source = orderedData.length ? orderedData : services;

      const reorderedItems = source.map((item, index) => ({
        id: item.id,
        priority: index,
      }));

      await updateServicePriority({
        reorderedItems,
      });

      message.success("Order Updated");

      const updatedFullData = source
        .map((item) => services.find((s) => s.id === item.id))
        .filter(Boolean);

      setServices(updatedFullData);
    } catch (err) {
      message.error("Reorder Failed");
    }
  };
  const handleChildSubmit = async (values) => {
    try {
      if (childEditMode) {
        await updateServiceDetails(childEditId, {
          title: values.title,
          description: values.description,
        });

        message.success("Updated");
      } else {
        await createServiceDetails({
          ...values,
          serviceId: parentId,
          image: imageBase64,
        });

        message.success("Added");
      }

      fetchChildServices(parentId);

      childForm.resetFields();
      setImageBase64("");
      setFileList([]);
      setChildEditMode(false);
    } catch {
      message.error("Error");
    }
  };

  // Delete

  const handleDelete = async (id) => {
    try {
      await deleteService(id);
      message.success("Deleted");
      fetchServices(currentPage);
    } catch {
      message.error("Delete failed");
    }
  };

  // Image update

  const handleImageChange = (file, id) => {
    const reader = new FileReader();

    reader.readAsDataURL(file);

    reader.onload = async () => {
      try {
        await updateServiceImage(id, {
          image: reader.result,
        });

        message.success("Image Updated");
        fetchServices(currentPage);
      } catch {
        message.error("Failed");
      }
    };
  };

  const handlePublish = async (id, checked) => {
    await updatePublishStatus(id, {
      isPublish: checked,
    });

    message.success(
      checked ? "Published Successfully" : "Unpublished Successfully",
    );

    fetchServices(currentPage);
  };

  // Table

  const columns = [
    {
      title: "Image",
      render: (_, record) => (
        <div style={{ textAlign: "center" }}>
          <Image
            src={record.image}
            width={60}
            height={60}
            preview={false}
            style={{ objectFit: "cover",cursor: "pointer" }}
            onClick={() => {
              setPreviewImage(record.image);
              setPreviewOpen(true);}}
          />
          <br></br>
          <br></br>
          <Upload
            showUploadList={false}
           beforeUpload={(file) => {
  const reader = new FileReader();

  reader.onload = () => {
    const base64 = reader.result;

    setImageBase64(base64);
    setImagePreview(base64);

    message.success("Image uploaded");
  };

  reader.readAsDataURL(file);
  return false;
}}
          >
            <Button size="small" icon={<UploadOutlined />}>
              Update
            </Button>
          </Upload>
        </div>
      ),
    },

    {
      title: "Title",
      dataIndex: "title",
    },

    {
      title: "Description",
      dataIndex: "description",
    },

    {
      title: "Publish",
      render: (_, record) => (
        <Switch
          checked={record.isPublish}
          onChange={(checked) => handlePublish(record.id, checked)}
        />
      ),
    },

    {
      title: "Action",
      render: (_, record) => (
        <Space direction="vertical">
          {/* Add child*/}

          <Button
            icon={<PlusOutlined />}
            onClick={() => {
              setParentId(record.id);
              setIsChildModal(true);
              setChildEditMode(false);
              childForm.resetFields();

              fetchChildServices(record.id);
            }}
          >
            Add
          </Button>

          <Button
            icon={<EditOutlined />}
            onClick={() => {
              setIsEditMode(true);
              setEditId(record.id);
              setIsModalVisible(true);

              form.setFieldsValue({
                title: record.title,
                description: record.description,
              });
            }}
          >
            Edit
          </Button>

          <Popconfirm title="Delete?" onConfirm={() => handleDelete(record.id)}>
            <Button danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Layout>
      <Content style={{ padding: 20 }}>
        <Card title={<Title level={3}>Services</Title>}>
          <Button
            type="primary"
           onClick={() => {
  setIsModalVisible(true);
  setIsEditMode(false);
  form.resetFields();

  setFileList([]);
  setImageBase64("");
  setImagePreview(null); // ✅ important
}}
          >
            Add Service
          </Button>
         <Button
  style={{ marginLeft: 10 }}
  icon={<DragOutlined />}
  onClick={async () => {
    if (!isOrderView) {
      const res = await getServices(1, 1000);

      const allServices = res?.data?.data || [];
      
      setServices(allServices);

      setOrderedData(
        allServices.map((i) => ({
          id: i.id,
          image: i.image, 
          title: i.title,
        })) 
      );
    } else {
      fetchServices(currentPage);
    }

    setIsOrderView(!isOrderView);
  }}
>
  {isOrderView ? "Back to List" : "Reorder"}
</Button>
          {isOrderView && (
            <Button
              type="primary"
              style={{ marginLeft: 10 }}
              onClick={handleSaveOrder}
            >
              Save Order
            </Button>
          )}
          <br></br>
          <br></br>
          {isOrderView ? (
            <ImageGridSorter
              data={services.map((i) => ({
                id: i.id,
                image: i.image,
                title: i.title,
                subtitle: i.description,
              }))}
              onChange={(newOrder) => {
                setOrderedData(newOrder);

                const updated = newOrder.map((item) =>
                  services.find((s) => s.id === item.id),
                );

                setServices(updated);
              }}
            />
          ) : (
            <Table
              columns={columns}
              dataSource={services}
              rowKey="id"
              loading={loading}
              pagination={{
                current: currentPage,
                total: totalItems,
                pageSize: pageSize,
                onChange: (page) => fetchServices(page),
              }}
            />
          )}
        </Card>
      </Content>

      {/* Model */}

      <Modal
        open={isModalVisible}
        onCancel={() => {
  setIsModalVisible(false);
  form.resetFields();

  setFileList([]);
  setImageBase64("");
  setImagePreview(null); // ✅ reset
}}
        footer={null}
        title={isEditMode ? "Edit Service" : "Add Service"}
      >
        <Form layout="vertical" form={form} onFinish={handleSubmit}>
          <Form.Item name="title" label="Title" rules={[{ required: true }]}>
            <Input />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
            rules={[{ required: true }]}
          >
            <TextArea rows={4} />
          </Form.Item>

          {!isEditMode && (
            <Form.Item label="Image">
              <Upload
                fileList={fileList}
                maxCount={1}
                beforeUpload={(file) => {
                  const reader = new FileReader();

                  reader.readAsDataURL(file);

                  reader.onload = () => {
                    setImageBase64(reader.result);
                  };

                  setFileList([file]);
                  return false;
                }}
                onRemove={() => {
                  setFileList([]);
                  setImageBase64("");
                }}
              >
                <Button icon={<UploadOutlined />}>Upload Image</Button>
              </Upload>
            </Form.Item>
          )}

          {!isEditMode && (
            <Form.Item name="isPublish" valuePropName="checked" label="Publish">
              <Switch />
            </Form.Item>
          )}

          <Button type="primary" htmlType="submit" block>
            Save
          </Button>
        </Form>
      </Modal>
      <Modal
        open={isChildModal}
        onCancel={() => {
  setIsChildModal(false);
  childForm.resetFields();

  setImageBase64("");
  setImagePreview(null);
}}
        footer={null}
        width={800}
        title="Add Sub Service"
      >
        <div style={{ display: "flex", gap: "20px" }}>
          <div style={{ width: "50%" }}>
            <Form
              layout="vertical"
              form={childForm}
              onFinish={handleChildSubmit}
            >
              <Form.Item
                name="title"
                label="Title"
                rules={[{ required: true }]}
              >
                <Input />
              </Form.Item>

              <Form.Item
                name="description"
                label="Description"
                rules={[{ required: true }]}
              >
                <Input.TextArea rows={3} />
              </Form.Item>

              {!childEditMode && (
                <Form.Item label="Image">
                 <Upload
  fileList={fileList}
  maxCount={1}
  beforeUpload={(file) => {
    const reader = new FileReader();

    reader.readAsDataURL(file);

    reader.onload = () => {
      const base64 = reader.result;

      setImageBase64(base64);
      setImagePreview(base64); // ✅ preview

    };

    setFileList([file]);
    return false;
  }}
  onRemove={() => {
    setFileList([]);
    setImageBase64("");
    setImagePreview(null); // ✅ reset
  }}
>
  <Button icon={<UploadOutlined />}>
    {imagePreview ? "Change Image" : "Upload Image"}
  </Button>
</Upload>

{/* ✅ Preview */}
{imagePreview && (
  <div style={{ marginTop: 10 }}>
    <img
      src={imagePreview}
      alt="preview"
      style={{ width: 80, height: 80, borderRadius: 8 }}
    />
  </div>
)}
                </Form.Item>
              )}

              {!childEditMode && (
                <Form.Item
                  name="isPublish"
                  valuePropName="checked"
                  label="Publish"
                >
                  <Switch />
                </Form.Item>
              )}

              <Button type="primary" htmlType="submit" block>
                Save
              </Button>
            </Form>
          </div>

          <div style={{ width: "50%", maxHeight: "400px", overflowY: "auto" }}>
            <Title level={5}>Added Items</Title>

            {childServices.map((item) => (
              <Card key={item.id} style={{ marginBottom: 10 }}>
                <Image src={item.image} width={70} preview={false} />

                <br></br>
                <br></br>
                <Upload
                  showUploadList={false}
                  beforeUpload={(file) => {
                    const reader = new FileReader();

                    reader.onload = async () => {
                      await updateServiceDetailsImage(item.id, {
                        image: reader.result,
                      });

                      message.success("Image Updated");
                      fetchChildServices(parentId);
                    };

                    reader.readAsDataURL(file);
                    return false;
                  }}
                >
                  <Button size="small" icon={<UploadOutlined />}>
                    Update
                  </Button>
                </Upload>

                <div style={{ textAlign: "center", marginTop: -30 }}>
                  <p style={{ fontWeight: "bold", marginBottom: 5 }}>
                    {item.title}
                  </p>
                  <p style={{ color: "#666" }}>{item.description}</p>
                </div>

                <Switch
                  checked={item.isPublish}
                  onChange={async (checked) => {
                    await updateServiceDetailsPublish(item.id, {
                      isPublish: checked,
                    });

                    message.success(
                      checked
                        ? "Published Successfully"
                        : "Unpublished Successfully",
                    );
                    fetchChildServices(parentId);
                  }}
                />

                <Space>
                  <br></br>
                  <Button
                    size="small"
                    onClick={() => {
                      setChildEditMode(true);
                      setChildEditId(item.id);

                      childForm.setFieldsValue({
                        title: item.title,
                        description: item.description,
                      });
                    }}
                  >
                    <EditOutlined /> Edit
                  </Button>

                  <Popconfirm
                    title="Delete?"
                    onConfirm={async () => {
                      await deleteServiceDetails(item.id);
                      message.success("Deleted");
                      fetchChildServices(parentId);
                    }}
                  >
                    <Button danger size="small">
                      <DeleteOutlined /> Delete
                    </Button>
                  </Popconfirm>
                </Space>
              </Card>
            ))}
          </div>
        </div>
      </Modal>
        {/* image preview */}
            
                    <Modal
                      open={previewOpen}
                      footer={null}
                      onCancel={() => setPreviewOpen(false)}
                    >
                      <Image src={previewImage} />
                    </Modal>
    </Layout>
  );
};

export default Services;
